import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@clari/ui'
import { ErrorBanner } from '@/components/ErrorBanner'
import { apiErrorMessage } from '@/lib/api-error'
import { adminApi } from '@/lib/admin-api'
import { formatDate } from '@/lib/format'

export function Reviews() {
  const { t, i18n } = useTranslation('admin')
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const query = useQuery({
    queryKey: ['admin-reviews', page],
    queryFn: () => adminApi.listReviews(page),
  })
  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1

  async function act(fn: () => Promise<unknown>) {
    setActionError(null)
    setBusy(true)
    try {
      await fn()
      await qc.invalidateQueries({ queryKey: ['admin-reviews'] })
    } catch (e) {
      setActionError(apiErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold">{t('reviews.queue_title')}</h1>
      <p className="text-text-muted">{t('reviews.queue_subtitle')}</p>

      {query.isError ? <ErrorBanner message={apiErrorMessage(query.error)} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {query.isLoading ? <p className="text-text-muted">{t('common.loading')}</p> : null}

      {query.data && query.data.items.length === 0 ? (
        <p className="text-text-muted">{t('reviews.empty')}</p>
      ) : null}

      <div className="flex flex-col gap-3">
        {query.data?.items.map((r) => (
          <div key={r.id} className="rounded-lg border border-border p-4">
            <div className="flex items-center justify-between">
              <Link to={`/accounts/${r.account.id}`} className="font-mono text-sm hover:underline">
                {r.account.iban}
              </Link>
              <span className="text-xs text-text-muted">{r.owner.email}</span>
            </div>
            <p className="mt-1 text-sm font-medium">
              {t(`review_types.${r.type}`, { defaultValue: r.type })}
            </p>
            {r.reason_internal ? <p className="text-xs text-warning">{r.reason_internal}</p> : null}
            <p className="mt-1 text-xs text-text-subtle">
              {t('reviews.started')} {formatDate(r.started_at, i18n.language)}
            </p>
            {r.escalated_at ? (
              <p className="text-xs text-warning">{t('reviews.escalated')}</p>
            ) : null}
            <div className="mt-2 flex gap-2">
              <Button size="sm" disabled={busy} onClick={() => act(() => adminApi.resolveReview(r.id))}>
                {t('reviews.resolve')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => act(() => adminApi.assignReview(r.id))}
              >
                {t('reviews.assign_me')}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {t('common.prev')}
        </button>
        <span className="text-text-muted">{t('common.page', { page })}</span>
        <button
          type="button"
          className="text-text-muted hover:text-text disabled:opacity-40"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  )
}
